import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { MediaTypes, useStartAPetitionPageState } from '#src/app/pages/startAPetitionV2/pageContext';
import {
	generateAiPetitionImage as createImageJob,
	aiImageForPetitionJob as fetchImageJob,
} from '#src/app/pages/startAPetitionV2/shared/api';
import { useAsyncJob } from '#src/app/shared/hooks/asyncJob';
import { useUploader } from '#src/app/shared/hooks/uppyUploader';

type CreateImageJobParams = Parameters<typeof createImageJob>[0];

type Options = Pick<Parameters<typeof useAsyncJob>[0], 'enabled'>;
export function useImageJob(options: Options = {}) {
	const { data, actions } = useStartAPetitionPageState();
	const uploader = useUploader(() => {});
	const track = useTracking();
	const utilityContext = useUtilityContext();

	const jobId = data.draft?.metadata?.imageJobId;
	const generationCount = data.draft?.metadata?.imageGenerationCount ?? 0;

	const asyncJob = useAsyncJob({
		jobId,
		timeout: 60000,
		mutationFn: async (input: CreateImageJobParams) => ({
			jobId: (await createImageJob(input, utilityContext, track)).responseId,
		}),
		queryFn: async (id) => fetchImageJob(id, uploader, utilityContext),
		onSuccess: (imageData) => {
			if (imageData.status !== 'COMPLETED') return;

			void track('sap_ai_image_generate_loaded');

			const image = { url: imageData.image, type: MediaTypes.AI };
			const draftData = {
				recommendedDraft: { image },
				petitionDraft: { image },
				metadata: { imageJobId: null },
			};

			uploader.clearImage();

			actions.send({ type: 'patch', draft: draftData });
		},
		...options,
	});

	const wrappedMutate = (input: CreateImageJobParams) => {
		const metadata = { imageGenerationCount: generationCount + 1 };
		actions.send({ type: 'patch', draft: { metadata } });

		asyncJob.mutate(input, {
			onSuccess: (d) => {
				actions.send({ type: 'patch', draft: { metadata: { imageJobId: d.jobId } } });
			},
		});
	};

	const wrappedMutateAsync = async (input: CreateImageJobParams) => {
		const metadata = { imageGenerationCount: generationCount + 1 };
		actions.send({ type: 'patch', draft: { metadata } });

		const asyncJobResult = await asyncJob.mutateAsync(input);
		actions.send({ type: 'patch', draft: { metadata: { imageJobId: asyncJobResult.jobId } } });

		return asyncJobResult;
	};

	return { ...asyncJob, mutate: wrappedMutate, mutateAsync: wrappedMutateAsync };
}
